import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

/**
 * ClubbieDropdown - JSX component with Tailwind CSS
 * Custom dropdown with single/multi-select support
 * 
 * Features:
 * - Single and multi-select modes
 * - Click outside to close
 * - Custom styling with error states
 * - Radio buttons for single-select, checkboxes for multi-select
 * 
 * @param {string} label - Dropdown label
 * @param {string|array} value - Selected value(s)
 * @param {array} options - Available options
 * @param {function} onChange - Selection change handler
 * @param {string} error - Error message
 * @param {string} suffix - Text suffix for options
 * @param {boolean} required - Required field indicator
 * @param {string} className - Additional CSS classes
 * @param {string} name - Unique dropdown identifier
 * @param {string} openDropdown - Currently open dropdown name
 * @param {function} setOpenDropdown - Open state handler
 * @param {boolean} isMulti - Enable multi-select mode
 */

const ClubbieDropdown = ({
  label,
  value = "",
  options = [],
  onChange,
  error = null,
  suffix = "",
  required = false,
  className = "",
  name,
  openDropdown,
  setOpenDropdown,
  isMulti = false,
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpenDropdown]);

  const handleOptionClick = (option, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isMulti) {
      onChange(option);
    } else {
      onChange(option);
      setOpenDropdown(null);
    }
  };

  const isSelected = (option) => {
    if (isMulti) {
      return Array.isArray(value) && value.includes(option);
    }
    return value === option;
  };

  const isOpen = openDropdown === name;
  const displayValue = isMulti
    ? Array.isArray(value) && value.length > 0
      ? [...new Set(value)].join(", ")
      : "Select options"
    : value || "";

  return (
    <div className={`w-full ${className}`} data-open={isOpen}>
      {/* Label */}
      <label className="block text-[#FEFCEB] text-sm font-medium mb-2">
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Dropdown Container */}
      <div className="relative" ref={dropdownRef}>
        {/* Selected Value Display */}
        <div
          className={`w-full h-11 px-4 bg-[#021529] border rounded-full text-[#E5E5D6] text-sm outline-none cursor-pointer flex items-center justify-between transition-all ${
            error
              ? "border-red-500 hover:border-red-400 focus:border-red-500 focus:shadow-[0_0_0_2px_rgba(239,68,68,0.2)]"
              : "border-[#1a3a5c] hover:border-[#2196F3] focus:border-[#00E7FF] focus:shadow-[0_0_0_2px_rgba(0,231,255,0.2)]"
          }`}
          onClick={() => setOpenDropdown(isOpen ? null : name)}
          tabIndex={0}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setOpenDropdown(null);
            }
          }}
        >
          <span className="truncate">
            {displayValue} {!isMulti && suffix}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-xs mt-1 ml-4">{error}</div>
        )}

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-[#021529] border border-[#1a3a5c] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.3)] z-[1000] max-h-60 overflow-y-auto">
            {options.map((option) => (
              <div
                key={option}
                className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${
                  isSelected(option)
                    ? "bg-[#082441] text-[#00E7FF]"
                    : "text-[#E5E5D6] hover:bg-[#041a30]"
                }`}
                onClick={(e) => handleOptionClick(option, e)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleOptionClick(option, e);
                }}
              >
                {/* Checkbox for Multi-Select */}
                {isMulti ? (
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected(option)
                        ? "border-[#00E7FF] bg-[#00E7FF]"
                        : "border-[#333E48] bg-transparent"
                    }`}
                  >
                    {isSelected(option) && (
                      <svg
                        className="w-3 h-3 text-[#081725]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                ) : (
                  /* Radio Button for Single-Select */
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      value === option
                        ? "border-[#00E7FF]"
                        : "border-[#333E48]"
                    }`}
                  >
                    {value === option && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />
                    )}
                  </div>
                )}
                <span className="text-sm">
                  {option} {!isMulti && suffix}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

ClubbieDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  suffix: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  openDropdown: PropTypes.string,
  setOpenDropdown: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
};

export default ClubbieDropdown;
